import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, KeyValuePair, ConnectionPoint, ConnectionPointType } from '../../models';
import { IUserSettingsService, IStoreService } from '../../services';
import { guidEmpty, userSettings } from '../../common';
import { StoreElement, Store } from '../../stateManagement';
import { Unsubscribe } from 'redux';

interface IConnectionPointSelectController {
    properties: KeyValuePair<string, string>[];
    model: string;
    formController: angular.IFormController;
    fieldName: string;
}

class ConnectionPointSelectController implements IConnectionPointSelectController {
    canImport: boolean;
    filter: (d: any) => boolean;
    formController: angular.IFormController;
    fieldName: string;
    hasModel: boolean;
    model: string;
    onChange: (value: { connectionPoint: ConnectionPoint }) => void;
    properties: KeyValuePair<string, string>[];
    selectedConnectionPoint: ConnectionPoint;
    types: ConnectionPointType[];

    private _connectionPoints: ConnectionPoint[];
    private _unregister: Unsubscribe;

    static $inject = [
        '$translate',
        'storeService',
        'userSettingsService'
    ];

    constructor(
        private _$translate: angular.translate.ITranslateService,
        private _storeService: IStoreService,
        private _userSettingsService: IUserSettingsService
    ) {
    }

    $onInit() {
        this._unregister = this._storeService.register(
            store => this.loadConnectionPoints(store),
            [StoreElement.ConnectionPoints]
        );
    }

    $onDestroy() {
        this._unregister();
    }

    loadConnectionPoints(store: Store): void {
        const cpList = _.values(store.connectionPoints.dictionary);
        const preparedData = this.prepareData(cpList);
        const props = this.buildConnectionPointList(preparedData);
        this._connectionPoints = cpList;
        this.properties = this.prepareProperties(props);
    }

    prepareProperties(properties: KeyValuePair<string, string>[]): any[] {
        if (this.hasModel) {
            const app = this._$translate.instant('Application');
            properties.unshift({
                key: guidEmpty,
                value: `<${app}>`
            });
        }

        if (!this.model && properties && properties.length > 0) {
            let defaultSelected = properties[0].key;

            const savedSelected = this._userSettingsService
                .getSettingValue(userSettings.connectionPointSelected);
            if (savedSelected) {
                const cp = _.find(properties, p => p.key === savedSelected);
                if (cp) {
                    defaultSelected = savedSelected;
                }
            }
            this.model = defaultSelected;
            this.selectedConnectionPoint = _.find(this._connectionPoints, cp => cp.guid === this.model);
        }
        return properties;
    }

    prepareData(data: ConnectionPoint[]): ConnectionPoint[] {
        if (this.types) {
            data = data.filter(cp => _.some(this.types, t => t === cp.type));
        }
        if (this.filter) {
            data = data.filter(cp => this.filter({ model: cp }));
        }
        return data;
    }

    buildConnectionPointList(data: ConnectionPoint[]): KeyValuePair<string, string>[] {
        let importables = [];
        if (this.canImport) {
            importables = data.filter(cp => !!cp.tagDefinitionFile);
        }
        let items = importables.filter(i => data.indexOf(i) < 0)
            .map(d => {
                return {
                    key: d.connectionPoint.guid,
                    value: d.connectionPoint.name
                };
            });
        items = data.map(cp => {
            return {
                key: cp.guid,
                value: cp.name
            };
        }).concat(items);
        items = _.sortBy(items, i => i.value.toUpperCase());

        return items;
    }

    change() {
        this._userSettingsService.setSettingValue(
            userSettings.connectionPointSelected, this.model
        );
        if (this.model) {
            this.selectedConnectionPoint = _.find(this._connectionPoints, cp => cp.guid === this.model);
            if(this.onChange){
                this.onChange({connectionPoint: this.selectedConnectionPoint});
            }
        }
    }
}

export const wtConnectionPointSelect: ComponentDefinition = {
    componentId: 'wtConnectionPointSelect',
    controller: ConnectionPointSelectController,
    controllerAs: 'vm',
    template: require('./connectionPointSelect.component.html'),
    bindings: {
        model: '=',
        formController: '=',
        fieldName: '=',
        types: '=?',
        canImport: '=?',
        hasModel: '=?',
        filter: '&?',
        disabled: '=?',
        selectedConnectionPoint: '=?',
        onChange: '&?'
    }
};